import {
  createContext,
  useState,
  Dispatch,
  ReactElement,
  useEffect,
} from "react";
import Web3 from "web3";
import { ChainType, Wallet, WalletType } from "@src/core/models/wallet";

export interface MetaMaskWalletContextProps {
  metaMaskWallet?: Wallet;
  setMetaMaskWallet?: Dispatch<Wallet | undefined>;
}

interface MetaMaskWalletProviderProps {
  children: ReactElement;
}

export const MetaMaskWalletContext = createContext<MetaMaskWalletContextProps>(
  {}
);

export const EVENT_GET_BALANCE = "eth_getBalance";
export const EVENT_GET_CHAIN_ID = "eth_chainId";
export const EVENT_LIST_ACCOUNTS = "eth_accounts";
export const EVENT_REQUEST_ACCOUNTS = "eth_requestAccounts";
export const CALLBACK_ACCOUNTS_CHANGED = "accountsChanged";
export const CALLBACK_CHAIN_CHANGED = "chainChanged";

export const MetaMaskWalletProvider = ({
  children,
}: MetaMaskWalletProviderProps): JSX.Element => {
  const [wallet, setWallet] = useState<Wallet>();

  useEffect(() => {
    const hasMetaMaskExtension = !!getEthereum();

    if (!hasMetaMaskExtension) {
      showExtensionNotFoundWarning();
      return;
    }

    signWalletEvents(setWallet);
    setPreviouslyConnectedAccount(setWallet);
  }, [setWallet]);

  return (
    <MetaMaskWalletContext.Provider
      value={{ metaMaskWallet: wallet, setMetaMaskWallet: setWallet }}
    >
      {children}
    </MetaMaskWalletContext.Provider>
  );
};

export const signWalletEvents = (
  callback: (wallet: Wallet | undefined) => void
): void => {
  getEthereum().on(CALLBACK_ACCOUNTS_CHANGED, (accountsList: string[]) => {
    handleAccountChange(accountsList, callback);
  });

  getEthereum().on(CALLBACK_CHAIN_CHANGED, () => {
    getAccountAddress().then((accountsList: string[]) => {
      handleAccountChange(accountsList, callback);
    });
  });
};

export const promptMetaMaskWalletConnection = (): void => {
  const ethereum = getEthereum();

  if (!ethereum) {
    showExtensionNotFoundWarning();
    return;
  }

  ethereum
    .request({
      method: EVENT_REQUEST_ACCOUNTS,
    })
    .catch(console.log);
};

const setPreviouslyConnectedAccount = (
  callback: (wallet: Wallet | undefined) => void
): void => {
  return getEthereum()
    .request({ method: EVENT_LIST_ACCOUNTS })
    .then((accountsList: string[]) => {
      handleAccountChange(accountsList, callback);
    });
};

const handleAccountChange = async (
  accountsList: string[],
  callback: (wallet: Wallet | undefined) => void
): Promise<void> => {
  if (!callback) return;

  const accountId = accountsList[0];

  if (!accountId) {
    callback(undefined);

    return;
  }

  const accountBalance = await getAccountBalance(accountId);
  const chainId = await getAccountChainId(accountId);

  callback({
    balance: accountBalance,
    id: accountId,
    type: WalletType.MetaMask,
    chainId,
  });
};

const getAccountAddress = async () => {
  return getEthereum()
    .request({ method: EVENT_LIST_ACCOUNTS })
    .then((accountsList: string[]) => {
      return accountsList;
    })
    .catch(console.log);
};

const getAccountBalance = async (accountId: string) => {
  return getEthereum()
    .request({ method: EVENT_GET_BALANCE, params: [accountId, "latest"] })
    .then((balance: string) => {
      return Web3.utils.fromWei(balance, "ether");
    })
    .catch(console.log);
};

const getAccountChainId = async (accountId: string): Promise<ChainType> => {
  return getEthereum()
    .request({ method: EVENT_GET_CHAIN_ID, params: [accountId, "latest"] })
    .then((chainId: string) => {
      return chainId as ChainType;
    })
    .catch(console.log);
};

const showExtensionNotFoundWarning = (): void => {
  console.log("Please install the MetaMask extension on your browser.");
};

const getEthereum = () => (window as any).ethereum;
